/*
 * QR Code generator library (Kotlin port)
 *
 * Based on the Java implementation by Project Nayuki (MIT License)
 * https://www.nayuki.io/page/qr-code-generator-library
 */

package io.nayuki.qrcodegen

import kotlin.math.min

/**
 * A segment of character/binary/control data in a QR Code symbol.
 * Instances of this class are immutable.
 */
class QrSegment(
    val mode: Mode,
    val numChars: Int,
    data: BitBuffer
) {
    init {
        require(numChars >= 0) { "Invalid value" }
    }

    private val data: BitBuffer = data.copy()

    fun getData(): BitBuffer = data.copy()

    companion object {
        private val NUMERIC_REGEX = Regex("[0-9]*")
        private val ALPHANUMERIC_REGEX = Regex("[A-Z0-9 $%*+./:-]*")

        const val ALPHANUMERIC_CHARSET = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ $%*+-./:"

        fun makeBytes(data: ByteArray): QrSegment {
            val buffer = BitBuffer()
            data.forEach { byte ->
                buffer.appendBits(byte.toInt() and 0xFF, 8)
            }
            return QrSegment(Mode.BYTE, data.size, buffer)
        }

        fun makeNumeric(digits: CharSequence): QrSegment {
            require(isNumeric(digits)) { "String contains non-numeric characters" }
            val buffer = BitBuffer()
            var i = 0
            while (i < digits.length) {
                val chunkSize = min(digits.length - i, 3)
                val chunk = digits.subSequence(i, i + chunkSize).toString().toInt()
                buffer.appendBits(chunk, chunkSize * 3 + 1)
                i += chunkSize
            }
            return QrSegment(Mode.NUMERIC, digits.length, buffer)
        }

        fun makeAlphanumeric(text: CharSequence): QrSegment {
            require(isAlphanumeric(text)) { "String contains unencodable characters in alphanumeric mode" }
            val buffer = BitBuffer()
            var i = 0
            while (i <= text.length - 2) {
                val first = ALPHANUMERIC_CHARSET.indexOf(text[i])
                val second = ALPHANUMERIC_CHARSET.indexOf(text[i + 1])
                buffer.appendBits(first * 45 + second, 11)
                i += 2
            }
            if (i < text.length) {
                buffer.appendBits(ALPHANUMERIC_CHARSET.indexOf(text[i]), 6)
            }
            return QrSegment(Mode.ALPHANUMERIC, text.length, buffer)
        }

        fun makeSegments(text: CharSequence): List<QrSegment> {
            if (text.isEmpty()) return emptyList()
            return when {
                isNumeric(text) -> listOf(makeNumeric(text))
                isAlphanumeric(text) -> listOf(makeAlphanumeric(text))
                else -> listOf(makeBytes(text.toString().encodeToByteArray()))
            }
        }

        fun makeEci(assignVal: Int): QrSegment {
            require(assignVal >= 0) { "ECI assignment value out of range" }
            val buffer = BitBuffer()
            when {
                assignVal < (1 shl 7) -> buffer.appendBits(assignVal, 8)
                assignVal < (1 shl 14) -> {
                    buffer.appendBits(0b10, 2)
                    buffer.appendBits(assignVal, 14)
                }
                assignVal < 1_000_000 -> {
                    buffer.appendBits(0b110, 3)
                    buffer.appendBits(assignVal, 21)
                }
                else -> throw IllegalArgumentException("ECI assignment value out of range")
            }
            return QrSegment(Mode.ECI, 0, buffer)
        }

        fun isNumeric(text: CharSequence): Boolean = NUMERIC_REGEX.matches(text)

        fun isAlphanumeric(text: CharSequence): Boolean = ALPHANUMERIC_REGEX.matches(text)

        internal fun getTotalBits(segments: List<QrSegment>, version: Int): Int {
            var total = 0L
            for (segment in segments) {
                val countBits = segment.mode.numCharCountBits(version)
                if (segment.numChars >= (1 shl countBits)) {
                    return -1
                }
                total += 4L + countBits + segment.data.bitLength
                if (total > Int.MAX_VALUE) return -1
            }
            return total.toInt()
        }
    }

    enum class Mode(
        val modeBits: Int,
        private val charCountBits: IntArray
    ) {
        NUMERIC(0x1, intArrayOf(10, 12, 14)),
        ALPHANUMERIC(0x2, intArrayOf(9, 11, 13)),
        BYTE(0x4, intArrayOf(8, 16, 16)),
        KANJI(0x8, intArrayOf(8, 10, 12)),
        ECI(0x7, intArrayOf(0, 0, 0));

        fun numCharCountBits(version: Int): Int {
            require(version in QrCode.MIN_VERSION..QrCode.MAX_VERSION)
            return charCountBits[(version + 7) / 17]
        }
    }
}
